import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  msaPhaseSelector,
  provinceSelector,
} from '../../redux/selectors/filtersSelectors';
import {
  resetMsaPause,
  resetMsaPhase,
  updateMsaPause,
  updateMsaPhase,
} from '../../redux/reducers/filtersReducer';

import BeginMSA from './BeginMSA/BeginMSA';
import { MSAFilterWrapper } from './styles';

const MatchMade = React.lazy(() => import('./MatchMade/MatchMade'));
const PauseScreen = React.lazy(() => import('./PauseScreen/PauseScreen'));

const PhaseScreen = React.lazy(() => import('./PhaseScreen/PhaseScreen'));

interface Props {
  msaData: Record<string, any>;
  activeFilterUpdate: (...args: any[]) => void;
  resetFilters: (string?) => void;
  setIsMsaActive: (boolean) => void;
  isMsaActive: boolean;
  province: string;
  location: Record<string, any>;
  allPhones: Record<string, any>[];
  locale: string;
}

const MSAFilter = ({
  msaData,
  activeFilterUpdate,
  resetFilters,
  setIsMsaActive,
  isMsaActive,
  province,
  location,
  allPhones,
  locale,
}: Props) => {
  const dispatch = useDispatch();
  const currentProvince = useSelector(provinceSelector);
  const msaPhase = useSelector(msaPhaseSelector);
  const changePhase = useCallback((x) => {
    dispatch(updateMsaPhase(x));
  }, [dispatch]);

  // ======= DATA LAYER =========
  // Submits where a user quit to analytics
  const dataLayerSubmit = useCallback((event) => {
    window.dataLayer = window.dataLayer || [];
    const data = {
      event,
      step:
        // If not on final match made phase, send category. Else send "match made".
        msaPhase !== -1
          ? `${msaData.msaSteps[msaPhase].smartFilter.cmsName}Selection`
          : 'Match made screen',
    };
    window.dataLayer.push(data);
  }, [msaData.msaSteps, msaPhase]);
  // ========= END OF DATA LAYER =============

  // Reset MSA phase and pause and remove from sessionStorage
  const resetMSA = useCallback((initalLoad?: boolean) => {
    if (!initalLoad) {
      dataLayerSubmit('msaDismiss');
    }
    dispatch(resetMsaPhase());
    changePhase(0);
    dispatch(resetMsaPause());
    setOnPause(false);
  }, [changePhase, dataLayerSubmit, dispatch]);

  // When user completes MSA and wants to start the process over
  const startOver = () => {
    resetFilters('startOver');
    changePhase(1);
  };

  // Pause feature - persists between page navigation
  // If user has initiated MSA flow, MSA will be paused between page navs
  const initialPauseValue = () => {
    if (msaPhase !== 0) {
      return true;
    }
    return false;
  };
  const [onPause, setOnPause] = useState(initialPauseValue);

  useEffect(() => {
    dispatch(updateMsaPause(onPause));
    // TODO [2025-06-10] TMSP-3195 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPause]);

  // When phase updates, if MSA is active setIsMsaActive to true, and put MSA phase in redux.
  // This tells "x phones found" where to display
  // This allows user to click a phone and come back to phone list with MSA still active
  useEffect(() => {
    if (msaPhase !== 0) {
      setIsMsaActive(true);
      dispatch(updateMsaPhase(msaPhase));
    } else {
      setIsMsaActive(false);
    }
    // TODO [2025-06-10] TMSP-3195 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msaPhase]);

  // If isMsaActive is false and there are no storedFilters in sessionStorage
  // Reset MSA as smart filters have been reset by user
  useEffect(() => {
    if (!isMsaActive && msaPhase !== undefined && msaPhase !== 0) {
      resetMSA();
    }
    // TODO [2025-06-10] TMSP-3195 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMsaActive]);

  // When province updates, check it against local storage (users saved preference)
  // If it's the same, user has navigated pages and we should not reset the MSA
  // If it's different, they have changed provinces and we reset MSA as filter options change
  useEffect(() => {
    if (
      typeof window !== 'undefined'
      && window.localStorage.savedProvince !== currentProvince
    ) {
      resetMSA(true);
    }
    // TODO [2025-06-10] TMSP-3195 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province]);

  // When page mounts, if an MSA phase is in redux and MSA isn't on pause, restore phase
  useEffect(() => () => {
    dispatch(resetMsaPhase());
    // TODO [2025-06-10] TMSP-3195 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When page mounts and is passed an MSA phase, user has initiated "find your mobile match" button
  // Reset filters, initialize MSA and set phase to local storage
  useEffect(() => {
    if (location?.state?.MSAPhase) {
      resetFilters();
      changePhase(location.state.MSAPhase);
      dispatch(updateMsaPhase(location.state.MSAPhase));
      if (typeof window !== 'undefined') {
        const data = {
          event: 'MSAlaunch',
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data);
      }
    }
    // TODO [2025-06-10] TMSP-3195 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  // ==============================
  // RETURN
  // ==============================

  return (
    <MSAFilterWrapper>
      {/* Default Screen */}
      {!onPause && msaPhase === 0 && (
        <BeginMSA msaData={msaData.msaSteps[0]} resetFilters={resetFilters} />
      )}
      <Suspense>
        {/* Phases */}
        {!onPause && msaPhase >= 1 && (
          <PhaseScreen
            msaData={msaData.msaSteps[msaPhase]}
            mobilist={msaData.mobilistImage}
            activeFilterUpdate={activeFilterUpdate}
            closeHandler={resetMSA}
            setOnPause={setOnPause}
            msaStepCount={msaData.msaSteps.length - 1}
            selectionError={msaData.stepChoiceSelectionError}
            noPhonesError={msaData.noPhonesFoundError}
            tipImage={msaData.tipImage}
            allPhones={allPhones}
            locale={locale}
          />
        )}
      </Suspense>
      {/* Final Screen */}
      {!onPause && msaPhase === -1 && (
        <Suspense>
          <MatchMade
            msaData={msaData.msaMatchMadeStep}
            mobilist={msaData.mobilistImage}
            closeHandler={resetMSA}
            startOver={startOver}
          />
        </Suspense>
      )}
      {/* Pause Screen */}
      {onPause && (
        <Suspense>
          <PauseScreen
            msaData={msaData.msaPauseStep}
            mobilist={msaData.mobilistImage}
            setOnPause={setOnPause}
          />
        </Suspense>
      )}
    </MSAFilterWrapper>
  );
};

export default MSAFilter;
